/* eslint-disable i18next/no-literal-string */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DOMPurify from 'dompurify';
import { Box, Button, Chip, CircularProgress, Divider, Drawer, Typography } from '@mui/material';
import { casesActions } from 'store/redux-store/cases/slice';
import { selectCaseDetailLoading, selectCasesServiceFailure, selectSelectedCase } from 'store/redux-store/cases/selectors';
import { ICaseFeedItem } from 'app/models/Candidate/SupportCases';

interface CaseDetailDrawerProps {
  open: boolean;
  onClose: () => void;
}

const FeedItem = ({ item }: { item: ICaseFeedItem }) => {
  if (item.feedItemType === 'CreateRecordEvent') {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {item.createdDate} — {item.createdByName}
        </Typography>
        <Typography variant="body2" fontStyle="italic">
          Case record created
        </Typography>
      </Box>
    );
  }

  if (item.feedItemType === 'EmailMessageEvent' && item.htmlBody) {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {item.createdDate} — {item.createdByName}
        </Typography>
        <Box
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.htmlBody) }}
          sx={{ fontSize: '14px' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {item.createdDate} — {item.createdByName}
      </Typography>
      <Typography variant="body2">{item.body}</Typography>
    </Box>
  );
};

export const CaseDetailDrawer = ({ open, onClose }: CaseDetailDrawerProps) => {
  const dispatch = useDispatch();
  const selectedCase = useSelector(selectSelectedCase);
  const detailLoading = useSelector(selectCaseDetailLoading);
  const serviceFailure = useSelector(selectCasesServiceFailure);

  const handleClose = () => {
    onClose();
    dispatch(casesActions.clearSelectedCase());
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ sx: { width: 480, p: 2 } }}>
      {detailLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : serviceFailure && !selectedCase ? (
        <Box sx={{ p: 2 }}>
          <Typography color="error">Failed to load case details. Please try again.</Typography>
          <Button size="small" onClick={handleClose} sx={{ mt: 1 }}>
            Back to Support Cases
          </Button>
        </Box>
      ) : selectedCase ? (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h6">Support Case</Typography>
            <Chip label={selectedCase.caseNumber} size="small" />
          </Box>
          <Button size="small" onClick={handleClose} sx={{ mb: 2 }}>
            Back to Support Cases
          </Button>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">AMIE Placement ID</Typography>
              <Typography variant="body2">{selectedCase.amiePlacementId ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Owner</Typography>
              <Typography variant="body2">{selectedCase.ownerName ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Type</Typography>
              <Typography variant="body2">{selectedCase.type ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Issue Type</Typography>
              <Typography variant="body2">{selectedCase.issueType ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Sub Issue</Typography>
              <Typography variant="body2">{selectedCase.subIssue ?? '—'}</Typography>
            </Box>
          </Box>
          {selectedCase.description && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Description</Typography>
              <Typography variant="body2">{selectedCase.description}</Typography>
            </Box>
          )}
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Activity</Typography>
          {selectedCase.feeds.map((item, idx) => (
            <React.Fragment key={idx}>
              <FeedItem item={item} />
              {idx < selectedCase.feeds.length - 1 && <Divider sx={{ my: 1 }} />}
            </React.Fragment>
          ))}
        </Box>
      ) : null}
    </Drawer>
  );
};
